"use server"

import { refresh } from "next/cache"

import { cookies } from "next/headers"
import { ApiError, errorMessage, storeApi, type ActionState } from "@/lib/api"
import { CART_COOKIE } from "@/lib/config"
import { getInfos } from "@/lib/shop"

const COOKIE_OPTIONS = { httpOnly: true, sameSite: "lax" as const, path: "/", maxAge: 60 * 60 * 24 * 30, secure: process.env.NODE_ENV === "production" }

async function newCart(): Promise<string> {
  const infos = await getInfos()
  const { cart } = await storeApi<{ cart: { id: string } }>("/store/carts", {
    body: { region_id: infos.region?.id },
  })
  ;(await cookies()).set(CART_COOKIE, cart.id, COOKIE_OPTIONS)
  return cart.id
}

export async function setCartCookie(id: string) {
  ;(await cookies()).set(CART_COOKIE, id, COOKIE_OPTIONS)
}

async function usableCart(): Promise<string> {
  const id = (await cookies()).get(CART_COOKIE)?.value
  if (!id) return newCart()
  try {
    const { cart } = await storeApi<{ cart: { completed_at: string | null } }>(`/store/carts/${id}?fields=id,completed_at`)
    if (cart.completed_at) return newCart()
    return id
  } catch {
    return newCart()
  }
}

export async function ajouterAuPanier(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const variant_id = String(formData.get("variant_id") ?? "")
  const quantity = Math.max(1, Number(formData.get("quantity") ?? 1) || 1)
  const valeur = String(formData.get("personnalisation") ?? "").trim()
  try {
    const id = await usableCart()
    await storeApi(`/store/carts/${id}/line-items`, {
      body: {
        variant_id,
        quantity,
        ...(valeur ? { metadata: { personnalisation: { valeur } } } : {}),
      },
    })
    refresh()
    return { ok: true, message: "Ajouté au panier." }
  } catch (e) {
    if (e instanceof ApiError && /stock|inventory|quantity/i.test(e.message)) {
      return { error: "Cette quantité n’est plus disponible." }
    }
    return { error: errorMessage(e) }
  }
}

export async function modifierQuantite(lineId: string, quantity: number): Promise<ActionState> {
  const id = (await cookies()).get(CART_COOKIE)?.value
  if (!id) return { error: "Panier introuvable." }
  try {
    if (quantity <= 0) {
      await storeApi(`/store/carts/${id}/line-items/${lineId}`, { method: "DELETE" })
    } else {
      await storeApi(`/store/carts/${id}/line-items/${lineId}`, { body: { quantity } })
    }
    refresh()
    return { ok: true }
  } catch (e) {
    if (e instanceof ApiError && /stock|inventory|quantity/i.test(e.message)) {
      return { error: "Cette quantité n’est plus disponible." }
    }
    return { error: errorMessage(e) }
  }
}
